/**
 * Capa tipada de la SESIÓN EN VIVO (hito M0 · "hilo de acero").
 *
 * Métodos sobre {@link apiFetch} para el contrato de API compartido con el
 * backend. Todo el refresco casi-vivo es por POLLING corto (TanStack Query con
 * refetchInterval); NO hay WebSockets en F0 — llegan en F1.
 *
 * Contratos de datos respetados desde el consumo:
 *   C1  aportes INSERT-only (una corrección es un aporte nuevo con parent_id).
 *   C2  el artefacto liga a TODOS los aportes al cerrar.
 *   C6  cerrar congela el artefacto (snapshot + estado 'cerrado' + versión).
 *   C7  métricas por persona NO se exhiben en Pantalla (solo agregados).
 *
 * Prefijo /api/v1 y Bearer los resuelve lib/api.ts.
 */
import { apiFetch } from './api';

/* ============================ Estados =================================== */

/**
 * Estados de actividad de la máquina F1:
 *   preparada -> abierta -> pausada <-> abierta -> cerrada
 * 'pausada' llega con M1 (override del facilitador con autoridad de servidor).
 */
export type ActividadEstado = 'preparada' | 'abierta' | 'pausada' | 'cerrada';

/** Tipos de aporte de CAPTURA (herramienta) admitidos en M0 (enum del backend). */
export type AporteTipo = 'texto' | 'voto' | 'posicion' | 'conexion' | 'foto';

/**
 * Tipos de aporte de EVIDENCIA rica (M2): media levantada en territorio desde
 * el Bolsillo. Se crean por el endpoint multipart /aportes/media, no por la
 * captura de la herramienta. Su payload lleva {media_key, filename, mime, size}.
 */
export type AporteMediaTipo = 'audio' | 'foto' | 'archivo';

/** Universo completo de tipos que puede tener un Aporte (captura + evidencia).
 * `conexion` es primitiva del participante cuando el manifiesto la declara
 * (card sorting, Oleada 3) y curaduría del facilitador cuando no (ADR-0006). */
export type AporteTipoTodos = AporteTipo | AporteMediaTipo;

/** True si el aporte es evidencia con objeto en MinIO (audio/foto/archivo). */
export function esMedia(tipo: string): tipo is AporteMediaTipo {
  return tipo === 'audio' || tipo === 'foto' || tipo === 'archivo';
}

/* ============================ Lectura: procesos ========================= */

export interface ProcesoResumen {
  id: string;
  nombre: string;
  estado: string;
  problema_enunciado: string | null;
  /** Solo para el superadmin (ve todas las orgs): institución dueña del proceso. */
  org_nombre?: string | null;
}

export interface SesionResumen {
  id: string;
  /** Sesión contenedora de validaciones de documentos: no es jornada conducible. */
  validaciones?: boolean;
  fecha: string | null;
  estado: string;
}

/** Estado de una jornada tras conducirla (activar / cerrar). */
export interface SesionEstado {
  id: string;
  fecha: string | null;
  estado: string;
  proceso_id: string;
  /** Días del mismo proceso que volvieron a 'preparada' al activar este. */
  desactivadas: string[];
}

/** Resultado de copiar las actividades de una jornada a otra. */
export interface ClonarActividadesResultado {
  sesion_id: string;
  creadas: string[];
  borradas: string[];
}

export interface FaseResumen {
  id: string;
  orden: number;
  nombre: string;
  tipo: string;
  sesiones: SesionResumen[];
}

export interface ProcesoDetalle {
  id: string;
  nombre: string;
  estado: string;
  fases: FaseResumen[];
}

/* ============================ Lectura: sesión =========================== */

export interface ActividadResumen {
  id: string;
  orden: number;
  herramienta_codigo: string;
  herramienta_nombre: string;
  modo: string;
  estado: ActividadEstado;
  duracion_min: number | null;
  /** M2: true si es diario de campo (asíncrona; vive mientras su fase esté activa). */
  asincrona?: boolean;
  /** Título propio del bloque (config.titulo), si el compositor lo fijó. */
  titulo?: string | null;
}

/**
 * Entrada del diario de campo (M2): actividad asíncrona ABIERTA de la fase
 * activa del proceso. El participante aporta entre sesiones desde el Bolsillo;
 * al cambiar de fase deja de recibir aportes (regla metodológica del owner).
 */
export interface DiarioActividad {
  id: string;
  herramienta_codigo: string;
  herramienta_nombre: string;
  fase_id: string;
  fase_nombre: string;
  abierta_at: string | null;
}

export interface SesionDetalle {
  id: string;
  fase_id: string;
  /** Proceso dueño: navegar Pantalla→Cabina lleva ESTE proceso, no el adivinado. */
  proceso_id: string;
  estado: string;
  actividades: ActividadResumen[];
}

/** Una actividad dentro del recap de cierre de sesión (A23). */
export interface RecapActividad {
  id: string;
  titulo: string;
  familia: string | null;
  total_aportes: number;
  /** Muestra de lo producido (resúmenes del acta congelada, máx. 3). */
  destacados: string[];
  version_acta: number | null;
}

/** Recap al cerrar la sesión: qué dejó la sala, solo agregados (C7). */
export interface RecapSesion {
  sesion_id: string;
  estado: string;
  total_aportes: number;
  total_participantes: number;
  actividades: RecapActividad[];
}

/**
 * Branding resuelto por la cascada instancia→org→proceso (ADR-0003). Los
 * logos NUNCA van hardcodeados en plantillas: el siguiente tenant solo cambia
 * su config.
 */
export interface BrandingResuelto {
  logos: { url: string; etiqueta: string }[];
  atribucion?: string | null;
}

/**
 * Una participación del usuario (GET /mis-participaciones): el proceso donde
 * tiene membresía con su contexto y sus sesiones en orden cronológico.
 */
export interface MiParticipacion {
  proceso_id: string;
  proceso_nombre: string;
  proceso_estado: string;
  org_nombre: string;
  rol: string;
  grupo_nombre: string | null;
  sesiones: {
    id: string;
    fecha: string | null;
    estado: string;
    fase_nombre: string;
    /** Cantidad de aportes PROPIOS del usuario en esa sesión. */
    mis_aportes: number;
  }[];
}

/* ============================ Lectura: actividad ======================= */

export interface Xia {
  que: string;
  por_que: string;
  hacia: string;
}

/**
 * Configuración de captura en el ALIAS v1 (`herramienta.captura`). El backend
 * podía enviar un código simple ('texto' | 'voto' | 'posicion') o un objeto con
 * opciones/escala. {@link resolverCaptura} unifica ambas formas (y la v2) para
 * la UI. Se retira junto con los alias al cerrar D5b.
 */
export type Captura =
  | string
  | {
      tipo: string;
      /** Etiquetas de las opciones cuando tipo === 'voto'. */
      opciones?: string[];
      /** Rango y anclas cuando tipo === 'posicion'. */
      escala?: {
        min: number;
        max: number;
        min_etiqueta?: string;
        max_etiqueta?: string;
      };
      /** Sugerencia dentro del textarea cuando tipo === 'texto'. */
      placeholder?: string;
    };

/** Una primitiva de captura del manifiesto v2 (C3): tipo + esquema + textos UI. */
export interface CapturaManifiesto {
  tipo?: string | null;
  requerido?: boolean;
  /** Esquema declarativo del payload (lo aplica el backend; D6). */
  payload_schema?: Record<string, unknown>;
  /** Textos de interfaz que el manifiesto trae listos (dominio GT real).
   * `imagen` (Oleada 3): URL del fondo del plano de coordenadas. */
  ui?: { placeholder?: string; ayuda?: string; imagen?: string };
  /** Ancla de evaluación (ADR-0005 §3): "inputs.<clave>" del elemento evaluado. */
  sobre?: string | null;
  /**
   * Opciones RESUELTAS por el backend (Lote 4 · conexiones): cuando un campo
   * declara `opciones_desde = "inputs.<clave>"`, GET /actividades/{id} las
   * inyecta desde el artefacto cerrado de la actividad fuente. Ausente o
   * vacía → la primitiva usa su forma por defecto. Desde ADR-0005 cada
   * opción puede venir como string plano o como `{valor, etiqueta}`.
   */
  opciones?: (string | OpcionCruda)[];
  /**
   * Total real de opciones cuando el backend truncó al tope del input
   * (ADR-0005 §4): la UI avisa «mostrando N de M», nunca calla.
   */
  opciones_total?: number;
  /** Pasos del wizard / rondas dirigidas (ADR-0005 §2). */
  secciones?: SeccionCaptura[];
}

/** Un paso del wizard por secciones (ADR-0005 §2). */
export interface SeccionCaptura {
  id: string;
  titulo: string;
  ayuda?: string;
  /** Nombres de campos del payload_schema que componen el paso. */
  campos: string[];
}

/** Opción declarada en el manifiesto v3: valor del payload + etiqueta humana. */
export interface OpcionCruda {
  valor: string;
  etiqueta?: string;
}

/** Proyección declarada por el manifiesto v2: vista + opciones de dibujo. */
export interface ProyeccionManifiesto {
  vista?: string | null;
  opciones?: Record<string, unknown>;
  /** v1: la proyección era prosa descriptiva, no una vista declarada. */
  descripcion?: string | null;
}

/**
 * Manifiesto público de la herramienta (C3, esquema v2): los diez campos del
 * contrato. Los alias v1 del final ya NO los envía el backend actual (se
 * retiraron al cerrar D5b); se conservan tipados como retro-compat defensiva
 * para la ventana de deploy contra un backend anterior.
 */
export interface Herramienta {
  codigo: string;
  nombre: string;
  familia?: string | null;
  modos?: string[];
  duracion_min?: number | null;
  capturas?: CapturaManifiesto[];
  inputs?: Record<string, unknown>[];
  output?: Record<string, unknown>;
  secciones_que_alimenta?: number[];
  eventos_reconocimiento?: string[];
  xia: Xia;
  /** v2: objeto {vista, opciones}; string solo desde un backend anterior. */
  proyeccion?: ProyeccionManifiesto | string | null;
  /**
   * ADR-0005 §6: panel de conducción declarado. La Cabina resuelve el
   * componente por su registro (components/cabina/paneles.tsx); un panel
   * desconocido se muestra como aviso visible a quien conduce, nunca silencio.
   */
  conduccion?: { panel?: string | null; opciones?: Record<string, unknown> };
  /* ── Alias v1 (solo llegan de un backend anterior a D5b) ── */
  captura?: Captura;
  modo?: string | null;
}

export interface ActividadDetalle {
  id: string;
  sesion_id: string;
  estado: ActividadEstado;
  herramienta: Herramienta;
  duracion_min: number | null;
  /** M2: true si es diario de campo (ver {@link DiarioActividad}). */
  asincrona?: boolean;
  /** Título propio del bloque (config.titulo): «Cápsula 1: por qué así». */
  titulo?: string | null;
  /** Instrucción operativa SOLO para la Cabina (config.nota_cabina). */
  nota_cabina?: string | null;
  /** True si la actividad exige registrar el supervoto del decisor (config). */
  supervoto?: boolean;
  /**
   * Sección activa de la ronda dirigida (config.ronda, ADR-0005 §2): el
   * Bolsillo muestra SOLO esa sección; null/ausente = wizard a ritmo propio.
   */
  ronda?: string | null;
  /**
   * Modo de proyección fijado por el facilitador (config.proyeccion_modo):
   * 'explicar' proyecta la consigna completa sin aportes; 'tablero' el formato
   * con las participaciones; null/ausente = automático (explicar sin aportes,
   * tablero desde el primero).
   */
  proyeccion_modo?: 'explicar' | 'tablero' | null;
  abierta_at: string | null;
  cerrada_at: string | null;
  total_aportes: number;
  /**
   * Segundos acumulados en pausa (autoridad de servidor, M1). Se guarda en
   * actividad.config.pausa (jsonb) sin cambio de esquema. El detalle REST puede
   * omitirlo; el evento WS 'actividad_estado' siempre lo trae. El Temporizador
   * descuenta este acumulado del tiempo transcurrido para congelar en pausa.
   */
  pausa_acumulado_s?: number | null;
}

/* ============================ Lectura: aportes ========================== */

/** Transcripción adjunta a un aporte de audio (M2 · evidencia rica). */
export interface Transcripcion {
  texto: string;
  confianza: number;
}

export interface Aporte {
  id: string;
  tipo: AporteTipoTodos;
  payload: Record<string, unknown>;
  user_nombre: string | null;
  grupo_nombre: string | null;
  created_at: string;
  /** Clave del objeto en MinIO cuando el aporte es evidencia (audio/foto/archivo). */
  media_key?: string | null;
  /** Id de la transcripción ligada (audio ya transcrito); null mientras se procesa. */
  transcripcion_id?: string | null;
  /**
   * Texto transcrito ya disponible (llega por WS 'transcripcion_lista'). La
   * caché lo va parchando sobre el aporte para que sea citable en la Cabina.
   */
  transcripcion?: Transcripcion | null;
  /**
   * Moderación (M2): true si el facilitador lo ocultó. El aporte NUNCA se
   * borra (C1); el estado vive en `actividad.config.moderacion` y las
   * superficies deciden: Pantalla lo excluye, Cabina lo muestra atenuado.
   */
  oculto?: boolean;
}

/** Metadatos de media que el backend guarda en payload jsonb del aporte. */
export interface MediaPayload {
  media_key?: string;
  filename?: string;
  mime?: string;
  size?: number;
  idem?: string;
}

export interface AgregadoGrupo {
  grupo: string;
  n: number;
}

export interface Agregado {
  /** Conteo por tipo de aporte, p. ej. { texto: 12, voto: 4 }. */
  por_tipo: Record<string, number>;
  /** Lo ENVIADO por el botón (autoría directa) — la medida de participación. */
  total: number;
  por_grupo: AgregadoGrupo[];
  /**
   * Lo capturado por el micrófono de sala, contado APARTE (nunca sumado al
   * total): quien habla y además presiona el botón produciría dos registros
   * del mismo momento. Los dos canales se muestran, no se mezclan.
   */
  hablado?: { intervenciones: number; hablantes: number };
}

export interface AportesRespuesta {
  aportes: Aporte[];
  agregado: Agregado;
}

/* ============================ Acciones ================================= */

/** Respuesta de cierre: la actividad ya cerrada + el id del artefacto (C6). */
export interface CerrarRespuesta {
  actividad: ActividadDetalle;
  artefacto_id: string;
}

/** Cuerpo de un aporte nuevo (C1: INSERT-only). */
export interface NuevoAporte {
  tipo: AporteTipo;
  payload: Record<string, unknown>;
}

/* ============================ Métodos API ============================== */

export const sesionApi = {
  /** Procesos de la org del usuario. */
  procesos: () => apiFetch<ProcesoResumen[]>('/procesos'),

  /** Detalle de un proceso con sus fases y sesiones. */
  proceso: (id: string) => apiFetch<ProcesoDetalle>(`/procesos/${id}`),

  /** Detalle de una sesión con su agenda de actividades. */
  sesion: (id: string) => apiFetch<SesionDetalle>(`/sesiones/${id}`),

  /** Detalle de una actividad (incluye el manifiesto XIA de la herramienta). */
  actividad: (id: string) => apiFetch<ActividadDetalle>(`/actividades/${id}`),

  /**
   * Conducción de la JORNADA (facilitador). «Hoy corremos este día»: un
   * proceso corre uno a la vez, así que activar uno devuelve los demás a
   * 'preparada'. Sin esto, las superficies adivinaban el día.
   */
  iniciarSesion: (id: string) =>
    apiFetch<SesionEstado>(`/sesiones/${id}/iniciar`, { method: 'POST' }),

  /** Cierra la jornada: el código de sala deja de abrir. */
  cerrarSesion: (id: string) =>
    apiFetch<SesionEstado>(`/sesiones/${id}/cerrar`, { method: 'POST' }),

  /** Copia las actividades de otra jornada a esta (repetir un día). */
  clonarActividades: (
    id: string,
    origenSesionId: string,
    modo: 'agregar' | 'reemplazar' = 'agregar',
  ) =>
    apiFetch<ClonarActividadesResultado>(`/sesiones/${id}/clonar-actividades`, {
      method: 'POST',
      json: { origen_sesion_id: origenSesionId, modo },
    }),

  /**
   * Aportes de una actividad + agregado para Pantalla. `desde` (ISO) permite
   * traer solo lo nuevo en un poll; se omite para traer todo.
   */
  aportes: (id: string, desde?: string, mios?: boolean) => {
    const params = new URLSearchParams();
    if (desde) params.set('desde', desde);
    if (mios) params.set('mios', 'true');
    const qs = params.toString();
    return apiFetch<AportesRespuesta>(`/actividades/${id}/aportes${qs ? `?${qs}` : ''}`);
  },

  /** Facilitador: preparada -> abierta (409 si no está preparada). */
  abrir: (id: string) =>
    apiFetch<ActividadDetalle>(`/actividades/${id}/abrir`, { method: 'POST' }),

  /** Facilitador: abierta -> cerrada; congela el artefacto (C6). */
  cerrar: (id: string) =>
    apiFetch<CerrarRespuesta>(`/actividades/${id}/cerrar`, { method: 'POST' }),

  /**
   * Participante: crea un aporte INSERT-only (C1). 409 si no está abierta.
   * El `payload` puede incluir un token `idem` (uuid del cliente) para que el
   * backend deduplique reintentos offline (misma actividad + mismo idem => 200
   * con el aporte ya existente, sin duplicar). Ver {@link enviarAporte}.
   */
  postAporte: (id: string, cuerpo: NuevoAporte) =>
    apiFetch<Aporte>(`/actividades/${id}/aportes`, {
      method: 'POST',
      json: cuerpo,
    }),

  /* -------- Override del temporizador (facilitador · M1) ---------------- */

  /** Facilitador: abierta -> pausada. Congela el countdown (server-authoritative). */
  pausar: (id: string) =>
    apiFetch<ActividadDetalle>(`/actividades/${id}/pausar`, { method: 'POST' }),

  /** Facilitador: pausada -> abierta. Suma el tramo de pausa al acumulado. */
  reanudar: (id: string) =>
    apiFetch<ActividadDetalle>(`/actividades/${id}/reanudar`, { method: 'POST' }),

  /** Facilitador: ajusta la duración en +/- minutos (body {delta_min}). */
  ajustarTiempo: (id: string, deltaMin: number) =>
    apiFetch<ActividadDetalle>(`/actividades/${id}/tiempo`, {
      method: 'POST',
      json: { delta_min: deltaMin },
    }),

  /**
   * Facilitador: modera un aporte (M2). `ocultar` lo excluye de Pantalla y de
   * la propuesta; `restaurar` lo devuelve. El aporte jamás se borra (C1).
   */
  moderar: (actividadId: string, aporteId: string, accion: 'ocultar' | 'restaurar') =>
    apiFetch<{ aporte_id: string; oculto: boolean }>(
      `/actividades/${actividadId}/aportes/${aporteId}/moderar`,
      { method: 'POST', json: { accion } },
    ),

  /** Diario de campo (M2): actividades asíncronas abiertas de la fase activa. */
  diario: (procesoId: string) =>
    apiFetch<DiarioActividad[]>(`/procesos/${procesoId}/diario`),

  /* -------- Participante: mis participaciones --------------------------- */

  /**
   * Historial del participante: los procesos donde TIENE membresía, con su
   * rol/grupo y las sesiones (pasadas y futuras) de cada uno. Solo lo suyo:
   * un participante no ve procesos donde no es miembro.
   */
  misParticipaciones: () =>
    apiFetch<MiParticipacion[]>('/mis-participaciones'),

  /* -------- Sala: invitación y auto-registro (Lote 6) ------------------- */

  /**
   * Datos para invitar a la sala de una sesión: el código (UUID de la sesión),
   * el PIN COMPARTIDO del proceso (dato público de proyección; null si no se
   * fijó) y el BRANDING resuelto por la cascada instancia→org→proceso
   * (ADR-0003): logos institución/cooperante y texto de atribución, para que
   * la Pantalla los proyecte. Miembros de la org.
   */
  sala: (sesionId: string) =>
    apiFetch<{
      codigo_sesion: string;
      pin: string | null;
      branding: BrandingResuelto | null;
    }>(`/sesiones/${sesionId}/sala`),

  /** Facilitador: fija (o cambia) el PIN de sala compartido del proceso. */
  fijarPinSala: (procesoId: string, pin: string) =>
    apiFetch<{ pin: string }>(`/procesos/${procesoId}/sala`, {
      method: 'POST',
      json: { pin },
    }),

  /** Facilitador: marca/desmarca una actividad como diario de campo (asíncrona). */
  marcarAsincrona: (actividadId: string, activa: boolean) =>
    apiFetch<{ actividad_id: string; asincrona: boolean }>(
      `/actividades/${actividadId}/asincrona`,
      { method: 'POST', json: { activa } },
    ),

  /** Facilitador: fija (o levanta con null) la sección activa de la ronda (ADR-0005 §2). */
  marcarRonda: (actividadId: string, seccion: string | null) =>
    apiFetch<{ actividad_id: string; seccion: string | null }>(
      `/actividades/${actividadId}/ronda`,
      { method: 'POST', json: { seccion } },
    ),

  /** Facilitador: fija (o levanta con null) el modo de proyección de la Pantalla. */
  marcarProyeccion: (actividadId: string, modo: 'explicar' | 'tablero' | null) =>
    apiFetch<{ actividad_id: string; modo: string | null }>(
      `/actividades/${actividadId}/proyeccion`,
      { method: 'POST', json: { modo } },
    ),

  /** Recap de cierre de la sesión: qué dejó la sala (A23, solo agregados C7). */
  recap: (sesionId: string) => apiFetch<RecapSesion>(`/sesiones/${sesionId}/recap`),
};

/* ============================ Query keys =============================== */

/** Llaves estables de TanStack Query para invalidar/pollear con precisión. */
export const sesionKeys = {
  procesos: ['procesos'] as const,
  proceso: (id: string) => ['proceso', id] as const,
  sesion: (id: string) => ['sesion', id] as const,
  actividad: (id: string) => ['actividad', id] as const,
  aportes: (id: string) => ['aportes', id] as const,
  presencia: (sesionId: string) => ['presencia', sesionId] as const,
  diario: (procesoId: string) => ['diario', procesoId] as const,
};

/* ============================ Helpers ================================= */

/**
 * Regla de UN campo del `payload_schema.campos` del manifiesto. Es el mismo
 * esquema que el backend aplica en `validacion_aportes.py` (D6): si la captura
 * lo dibuja fiel, el aporte pasa la validación siempre.
 */
export interface CampoSchema {
  tipo?: 'string' | 'number' | string;
  requerido?: boolean;
  /** string: largo mín/máx · number: rango mín/máx. */
  min?: number;
  max?: number;
  /** Lista literal de opciones válidas: strings o `{valor, etiqueta}` (v3). */
  opciones?: (string | OpcionCruda)[];
  /** Rótulo humano del campo (ADR-0005 §1); sin él, la primitiva pone el suyo. */
  etiqueta?: string;
  /** Texto de ayuda bajo el rótulo, en el idioma del dominio. */
  ayuda?: string;
  /** Sugerencia dentro del input/textarea de ESTE campo. */
  placeholder?: string;
  /** Posición del campo en pantalla; sin él, el orden de declaración. */
  orden?: number;
  /**
   * El campo lo llena la máquina, no la persona: no se dibuja nunca.
   *
   * Lo estrena `bloque_id_estable` en la v4 de `validacion_documento` (el uuid
   * del párrafo que se tocó en el visor). Sin esta guarda, cualquier superficie
   * que dibuje el `payload_schema` de forma declarativa le pintaría a una
   * validadora una caja pidiéndole que escriba un uuid.
   */
  oculto?: boolean;
}

/** Opción normalizada para la UI: siempre con valor y etiqueta. */
export interface OpcionCampo {
  valor: string;
  etiqueta: string;
}

/** Normaliza una lista de opciones del manifiesto (strings o {valor, etiqueta}). */
export function normalizarOpciones(lista: (string | OpcionCruda)[] | undefined): OpcionCampo[] {
  if (!lista?.length) return [];
  const normales: OpcionCampo[] = [];
  for (const op of lista) {
    if (typeof op === 'string') normales.push({ valor: op, etiqueta: op });
    else if (op && typeof op.valor === 'string')
      normales.push({ valor: op.valor, etiqueta: op.etiqueta ?? op.valor });
  }
  return normales;
}

/**
 * Nombres de campos en el orden que el manifiesto declara (ADR-0005 §1):
 * `orden` explícito manda; sin él, la posición de declaración (estable).
 *
 * Los campos `oculto` quedan fuera: los llena la máquina y no se dibujan ni se
 * validan. Es la única salida de este listado, así que basta filtrar acá.
 */
export function nombresOrdenados(campos: Record<string, CampoSchema>): string[] {
  const nombres = Object.keys(campos).filter((n) => !campos[n]?.oculto);
  return nombres
    .map((nombre, i) => ({ nombre, orden: campos[nombre]?.orden ?? null, i }))
    .sort((a, b) => (a.orden ?? a.i + 1000) - (b.orden ?? b.i + 1000) || a.i - b.i)
    .map((x) => x.nombre);
}

/** Forma normalizada de la captura para que la UI no ramifique por tipo. */
export interface CapturaNormal {
  tipo: AporteTipo;
  opciones: OpcionCampo[];
  escala: { min: number; max: number; minEtiqueta: string; maxEtiqueta: string };
  placeholder: string;
  /** Instrucción del manifiesto (`ui.ayuda`); null → la primitiva pone la suya. */
  ayuda: string | null;
  /**
   * Campos que declara el `payload_schema` del manifiesto (D5c: captura
   * declarativa). Vacío en manifiestos v1 → la primitiva usa su forma simple.
   */
  campos: Record<string, CampoSchema>;
  /** Presupuesto de votos por persona (payload_schema.presupuesto), si aplica. */
  presupuesto: number | null;
  /** Total real de opciones si el backend truncó al tope del input; null = completas. */
  opcionesTotal: number | null;
  /** Pasos del wizard por secciones (ADR-0005 §2); vacío = formulario plano. */
  secciones: SeccionCaptura[];
  /**
   * True si la captura evalúa un elemento concreto (`capturas[].sobre`,
   * ADR-0005 §3): la UI exige elegir «sobre cuál» entre las `opciones`.
   */
  sobre: boolean;
  /** URL de la imagen de fondo del plano de coordenadas (ui.imagen); null = cuadrícula. */
  imagen: string | null;
}

/** Primitivas de captura que esta versión de la app sabe renderizar. */
const TIPOS_CAPTURA: readonly AporteTipo[] = [
  'texto',
  'voto',
  'posicion',
  'conexion',
  'foto',
];

/** Guard del enum: SOLO estos tipos tienen componente registrado (D5d). */
export function esTipoCaptura(valor: unknown): valor is AporteTipo {
  return typeof valor === 'string' && (TIPOS_CAPTURA as readonly string[]).includes(valor);
}

/**
 * Resultado de resolver la captura de una herramienta. La rama no soportada
 * OBLIGA a la UI a mostrar un estado visible (nunca render en blanco ni
 * payload por defecto — deuda D5d de la auditoría C3).
 */
export type CapturaResuelta =
  | { soportada: true; captura: CapturaNormal }
  | {
      soportada: false;
      tipoCrudo: string | null;
      /**
       * True cuando el manifiesto declara `capturas: []` DELIBERADAMENTE: es un
       * momento informativo (cápsula, pausa, lectura) — sin captura, con XIA y
       * timer. La UI muestra «seguí la proyección», no «no soportada».
       */
      informativa?: boolean;
    };

const VOTO_DEFECTO: OpcionCampo[] = [
  { valor: 'A favor', etiqueta: 'A favor' },
  { valor: 'En contra', etiqueta: 'En contra' },
  { valor: 'Me abstengo', etiqueta: 'Me abstengo' },
];

const CAPTURA_BASE: Omit<CapturaNormal, 'tipo'> = {
  opciones: VOTO_DEFECTO,
  escala: { min: 1, max: 5, minEtiqueta: 'Nada de acuerdo', maxEtiqueta: 'Muy de acuerdo' },
  placeholder: 'Escribí tu aporte en tus palabras…',
  ayuda: null,
  campos: {},
  presupuesto: null,
  opcionesTotal: null,
  secciones: [],
  sobre: false,
  imagen: null,
};

/** Secciones válidas del manifiesto (id + titulo + campos no vacíos). */
function _seccionesDe(lista: SeccionCaptura[] | undefined): SeccionCaptura[] {
  if (!lista?.length) return [];
  return lista.filter(
    (s) =>
      s &&
      typeof s.id === 'string' &&
      s.id.length > 0 &&
      typeof s.titulo === 'string' &&
      Array.isArray(s.campos) &&
      s.campos.length > 0,
  );
}

function _presupuestoDe(payloadSchema: unknown): number | null {
  if (payloadSchema == null || typeof payloadSchema !== 'object') return null;
  const p = (payloadSchema as { presupuesto?: { votos_por_persona?: unknown } }).presupuesto;
  const n = p?.votos_por_persona;
  return typeof n === 'number' && n > 0 ? n : null;
}

function _camposDe(payloadSchema: unknown): Record<string, CampoSchema> {
  if (payloadSchema == null || typeof payloadSchema !== 'object') return {};
  const campos = (payloadSchema as { campos?: unknown }).campos;
  if (campos == null || typeof campos !== 'object') return {};
  return campos as Record<string, CampoSchema>;
}

/**
 * Resuelve la captura de la herramienta VALIDANDO el enum de primitivas.
 * Lee primero `capturas[]` del manifiesto v2 (con sus textos `ui`); si no
 * está, cae al alias v1 (`captura` string u objeto). Un tipo fuera del enum
 * devuelve `soportada: false` con el tipo crudo para el mensaje al usuario.
 */
export function resolverCaptura(herramienta: Herramienta): CapturaResuelta {
  // Momento informativo: `capturas: []` explícito (cápsulas, pausas, lecturas).
  if (Array.isArray(herramienta.capturas) && herramienta.capturas.length === 0) {
    return { soportada: false, tipoCrudo: null, informativa: true };
  }
  // v2: la primera primitiva declarada (multi-captura llega con F3).
  const v2 = herramienta.capturas?.[0];
  if (v2 != null) {
    const tipo = v2.tipo ?? null;
    if (!esTipoCaptura(tipo)) return { soportada: false, tipoCrudo: tipo };
    return {
      soportada: true,
      captura: {
        ...CAPTURA_BASE,
        tipo,
        // Lote 4: opciones reales del artefacto fuente (las resuelve el
        // backend por bindings/cercanía); sin ellas, el defecto de la base.
        opciones: v2.opciones?.length ? normalizarOpciones(v2.opciones) : CAPTURA_BASE.opciones,
        placeholder: v2.ui?.placeholder ?? CAPTURA_BASE.placeholder,
        ayuda: v2.ui?.ayuda ?? null,
        campos: _camposDe(v2.payload_schema),
        presupuesto: _presupuestoDe(v2.payload_schema),
        opcionesTotal:
          typeof v2.opciones_total === 'number' && v2.opciones?.length
            ? v2.opciones_total
            : null,
        secciones: _seccionesDe(v2.secciones),
        sobre: typeof v2.sobre === 'string' && v2.sobre.startsWith('inputs.'),
        imagen: v2.ui?.imagen ?? null,
      },
    };
  }

  // v1 (alias): código simple u objeto con opciones/escala.
  const v1 = herramienta.captura;
  if (v1 == null) return { soportada: false, tipoCrudo: null };
  if (typeof v1 === 'string') {
    if (!esTipoCaptura(v1)) return { soportada: false, tipoCrudo: v1 };
    return { soportada: true, captura: { ...CAPTURA_BASE, tipo: v1 } };
  }
  if (!esTipoCaptura(v1.tipo)) return { soportada: false, tipoCrudo: v1.tipo ?? null };
  return {
    soportada: true,
    captura: {
      tipo: v1.tipo,
      opciones: v1.opciones?.length ? normalizarOpciones(v1.opciones) : CAPTURA_BASE.opciones,
      escala: v1.escala
        ? {
            min: v1.escala.min,
            max: v1.escala.max,
            minEtiqueta: v1.escala.min_etiqueta ?? CAPTURA_BASE.escala.minEtiqueta,
            maxEtiqueta: v1.escala.max_etiqueta ?? CAPTURA_BASE.escala.maxEtiqueta,
          }
        : CAPTURA_BASE.escala,
      placeholder: v1.placeholder ?? CAPTURA_BASE.placeholder,
      ayuda: null,
      campos: {},
      presupuesto: null,
      opcionesTotal: null,
      secciones: [],
      sobre: false,
      imagen: null,
    },
  };
}

// El payload de cada aporte lo construye SU componente de captura (registro
// D5b/D5c): la convención de claves del jsonb es parte de la primitiva, no de
// esta capa. `construirPayload` se retiró al entrar la captura declarativa.

/** Verbo humano de cada acción de curaduría (ADR-0006) para la Cabina. */
const ACCION_CURADURIA: Record<string, string> = {
  agrupar: 'agrupó',
  titular: 'tituló',
  ordenar: 'ordenó',
  severidad: 'marcó severidad de',
  responder: 'respondió a',
  descartar: 'descartó',
  atribuir: 'puso nombre a',
};

/** Claves de payload que no son contenido del aporte (sellos y mecánica). */
const CLAVES_MECANICA = new Set([
  'idem',
  'curaduria',
  'supervoto',
  'accion',
  'sobre',
  'seccion',
  'microfono_sala',
  'fuente',
  'hablante',
  'inicio_s',
  'fin_s',
  // Mecánica de media (evidencia): jamás son "el contenido" del aporte.
  'mime',
  'size',
  'filename',
  'media_key',
  'inicio_grabacion',
  'num_hablantes',
]);

/** «12:05» a partir de los segundos de una intervención del micrófono de sala. */
function minutoDe(segundos: unknown): string {
  const s = typeof segundos === 'number' ? segundos : 0;
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

/** Resumen humano de un aporte para mostrarlo en la lista de la Cabina. */
export function resumirAporte(aporte: Aporte): string {
  const p = aporte.payload ?? {};

  // Micrófono de sala: quién lo dijo y en qué minuto de la grabación.
  if (p.fuente === 'sala' && typeof p.texto === 'string') {
    const quien = typeof p.hablante === 'string' ? p.hablante : 'Alguien';
    return `${quien} (${minutoDe(p.inicio_s)}): ${p.texto}`;
  }
  if (p.microfono_sala) return 'Grabación de sala';

  if (aporte.tipo === 'texto' && typeof p.texto === 'string') return p.texto;
  if (aporte.tipo === 'voto' && typeof p.opcion === 'string') return p.opcion;
  if (aporte.tipo === 'posicion' && p.valor != null) return `Posición: ${String(p.valor)}`;

  // Evidencia (bug real: al recargar, la nota de voz de un participante se
  // resumía como su mecánica — "audio/webm · 483564 · …" — y él la daba por
  // perdida). Se nombra la evidencia y, si ya hay transcripción, se muestra.
  if (aporte.tipo === 'audio') {
    const t = aporte.transcripcion?.texto?.trim();
    return t ? `Nota de voz: ${t}` : 'Nota de voz — transcribiendo…';
  }
  if (aporte.tipo === 'foto') {
    return typeof p.filename === 'string' && p.filename ? `Foto · ${p.filename}` : 'Foto';
  }
  if (aporte.tipo === 'archivo') {
    return typeof p.filename === 'string' && p.filename
      ? `Archivo · ${p.filename}`
      : 'Archivo adjunto';
  }

  // Curaduría (ADR-0006): qué hizo el facilitador, sobre cuántos, con qué valor.
  if (aporte.tipo === 'conexion' && p.curaduria) {
    const n = Array.isArray(p.sobre) ? p.sobre.length : 0;
    const verbo = ACCION_CURADURIA[String(p.accion)] ?? 'curó';
    const valor = typeof p.valor === 'string' && p.valor.trim() ? `: «${p.valor.trim()}»` : '';
    return `Curaduría — ${verbo} ${n === 1 ? '1 aporte' : `${n} aportes`}${valor}`;
  }

  // Payload multi-campo (ADR-0005 §4): los valores legibles, no solo el primero
  // — una historia de usuario muestra sus tres partes, no una.
  const partes = Object.entries(p)
    .filter(
      ([clave, v]) =>
        !CLAVES_MECANICA.has(clave) &&
        ((typeof v === 'string' && v.trim().length > 0) || typeof v === 'number'),
    )
    .map(([, v]) => String(v).trim());
  if (partes.length === 0) return '(aporte)';
  return partes.slice(0, 3).join(' · ') + (partes.length > 3 ? ' …' : '');
}

/**
 * Cuenta aportes por opción/valor SIN exponer autores (apto para Pantalla, C7).
 * Sirve para el tablero de votos o el histograma de posición.
 */
export function conteoPorClave(aportes: Aporte[], clave: string): Map<string, number> {
  const m = new Map<string, number>();
  for (const a of aportes) {
    const v = (a.payload ?? {})[clave];
    if (v == null) continue;
    const k = String(v);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

/* ---------------------- Resolución de la sesión activa ------------------ */

/** Elige el proceso "vivo" de la org: prefiere estado 'activo'. */
export function elegirProcesoActivo(procesos: ProcesoResumen[]): ProcesoResumen | null {
  if (!procesos.length) return null;
  return procesos.find((p) => p.estado === 'activo') ?? procesos[0];
}

const SESION_VIVA = ['en_curso', 'abierta', 'activa', 'en_progreso'];

export interface OpcionesSesionActiva {
  /** Día elegido a mano en la Cabina (?sesion=): manda sobre todo lo demás. */
  elegidaId?: string | null;
  /** Día que nombró el código de sala del QR con el que entró la persona. */
  ancladaId?: string | null;
}

/**
 * Elige la sesión "actual" del proceso. Orden de autoridad, de más fuerte a
 * más débil:
 *
 *   1. La ELEGIDA a mano en la Cabina. Quien conduce sabe qué está mirando;
 *      vale incluso sobre una jornada cerrada (revisar el acta de ayer).
 *   2. La marcada EN CURSO desde la Cabina. Es la declaración viva de quien
 *      conduce: «hoy corremos este día».
 *   3. La ANCLADA por el QR con el que entró la persona, si no cerró.
 *   4. La primera no cerrada. 5. La última.
 *
 * Por qué el «en curso» le gana al QR (y no al revés): el ancla del QR se
 * captura en un momento pasado y sobrevive en el teléfono. En un sprint de 8
 * días, quien escaneó el día 3 y no cerró sesión abriría el día 3 para
 * siempre. La marca «en curso» es de AHORA y la pone quien conduce.
 *
 * Y por qué el QR sigue existiendo: cuando NADA está en curso —todas las
 * sesiones sembradas en 'preparada', que era el estado real del sprint CCJ—
 * la regla 4 elige el primer día del sprint, y el QR del día 3 llevaba a la
 * sala del día 1. El ancla arregla justo ese caso.
 */
export function elegirSesionActiva(
  proceso: ProcesoDetalle,
  opciones: OpcionesSesionActiva = {},
): SesionResumen | null {
  const fases = [...proceso.fases].sort((a, b) => a.orden - b.orden);
  const sesiones = fases.flatMap((f) => f.sesiones);
  if (!sesiones.length) return null;

  const elegida = opciones.elegidaId
    ? sesiones.find((s) => s.id === opciones.elegidaId)
    : undefined;
  const anclada = opciones.ancladaId
    ? sesiones.find((s) => s.id === opciones.ancladaId && s.estado !== 'cerrada')
    : undefined;

  return (
    elegida ??
    sesiones.find((s) => SESION_VIVA.includes(s.estado)) ??
    anclada ??
    sesiones.find((s) => s.estado !== 'cerrada') ??
    sesiones[sesiones.length - 1]
  );
}

/**
 * De una agenda, cuál es la actividad "en foco": la abierta; si no hay,
 * la primera preparada; si no, la última cerrada.
 */
export function elegirActividadFoco(actividades: ActividadResumen[]): ActividadResumen | null {
  if (!actividades.length) return null;
  const orden = [...actividades].sort((a, b) => a.orden - b.orden);
  return (
    orden.find((a) => a.estado === 'abierta') ??
    orden.find((a) => a.estado === 'preparada') ??
    orden[orden.length - 1]
  );
}
